import { SfxToggle, useSfx } from '@slingu/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { drawBackground, drawSlingu, drawGround, sizeCanvas } from './slingu-draw.js';

const HIGH_SCORE_ID = 'quiz-run';

export type QuizRunQuestion = {
  id: string;
  prompt: string;
  choices: string[];
  answerIndex: number;
  difficulty?: 1 | 2 | 3;
};

export type QuizRunProps = {
  questions: QuizRunQuestion[];
  onExit: (result: {
    score: number;
    distance: number;
    xp: number;
    correctCount: number;
    total: number;
  }) => void;
};

type Phase = 'menu' | 'playing' | 'question' | 'over';

// --- Mobile-first portrait world ---------------------------------------
const WORLD_W = 480;
const WORLD_H = 720;
const GROUND_Y = 620;
const GROUND_H = WORLD_H - GROUND_Y;
const PLAYER_X = 130;
const PLAYER_R = 24;

const GRAVITY = 2400;
const JUMP_V = -820;
const BASE_SPEED = 180;
const MAX_SPEED = 280;
const SPEED_RAMP_PER_SEC = 3;

const SPAWN_INTERVAL = 3.0;
const TRIGGER_OFFSET = 250;
// Jump-trigger offset tuned against JUMP_V=-820, G=2400, SPEED=180:
//   - airtime ≈ 0.683s (peak at t=0.342s, height 140px)
//   - obstacle enters player range ~(65-28)/180 ≈ 0.2s from trigger
//   - player clears obstacle for ~580ms window
const JUMP_TRIGGER_OFFSET = 65;
const RESULT_FLASH_MS = 360;

// Per-question timer (seconds). When it runs out the answer auto-commits
// as 'wrong' so Slingu tromba no obstáculo.
const ANSWER_TIME = 7;

type Obstacle = {
  x: number;
  y: number;
  w: number;
  h: number;
  question: QuizRunQuestion;
  answered: 'pending' | 'correct' | 'wrong';
  triggered: boolean;
  jumpFired: boolean;
  cleared: boolean;
};

/**
 * QuizRun — corrida infinita estilo Chrome Dino. Obstáculos vêm pela direita;
 * ao cruzar a linha de gatilho, o mundo pausa e a pergunta aparece em
 * overlay. Acerto = pulo automático (altura calculada para limpar o
 * obstáculo); erro/timeout = mundo retoma e Slingu tromba.
 *
 * Pure Canvas 2D — sem framework, imune a StrictMode.
 */
export function QuizRun({ questions, onExit }: QuizRunProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const [phase, setPhase] = useState<Phase>('menu');
  const [question, setQuestion] = useState<QuizRunQuestion | null>(null);
  const [hud, setHud] = useState({ score: 0, distance: 0, hearts: 3, correct: 0, total: 0 });
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);
  const [flash, setFlash] = useState<null | 'correct' | 'wrong'>(null);
  const [timerPct, setTimerPct] = useState(1);

  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  const poolIndexRef = useRef(0);
  const pool = useMemo(
    () => [...questions].sort(() => Math.random() - 0.5),
    [questions],
  );
  const nextQuestion = useCallback((): QuizRunQuestion => {
    const q = pool[poolIndexRef.current % pool.length]!;
    poolIndexRef.current += 1;
    return q;
  }, [pool]);

  // --- Refs that hold live game state (not re-rendered) -----------------
  const gameRef = useRef({
    paused: false,
    awaiting: false as false | { obstacle: Obstacle },
    scrollX: 0,
    speed: BASE_SPEED,
    playerY: GROUND_Y - PLAYER_R,
    playerVy: 0,
    grounded: true,
    obstacles: [] as Obstacle[],
    spawnTimer: 1.0,
    score: 0,
    distance: 0,
    hearts: 3,
    correct: 0,
    total: 0,
    runT: 0,
    blinkT: 2 + Math.random() * 2,
    blink: 0,
    alive: true,
    flashUntil: 0,
    lastResult: null as null | 'correct' | 'wrong',
    inputQueue: null as null | 'correct' | 'wrong',
    timerRemaining: 0,
  });

  const startedRef = useRef(false);
  // Single boolean that drives the game-loop lifecycle. Phase changes
  // (playing ↔ question) MUST NOT tear down the game.
  const running = phase === 'playing' || phase === 'question';

  useEffect(() => {
    if (!running) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;

    function resize() {
      if (!canvas) return;
      sizeCanvas(canvas, WORLD_W, WORLD_H);
    }
    resize();
    window.addEventListener('resize', resize);

    const g = gameRef.current;

    let lastT = performance.now();

    function frame(nowMs: number) {
      if (disposed) return;
      const dt = Math.min(0.05, (nowMs - lastT) / 1000);
      lastT = nowMs;
      update(dt);
      render();
      rafRef.current = requestAnimationFrame(frame);
    }

    function update(dt: number) {
      if (!g.alive) return;

      // While the pergunta is open, count down the answer timer. If it hits
      // zero with no input, queue a 'wrong' answer — Slingu will tromba
      // naturally at the obstacle.
      if (g.awaiting && !g.inputQueue) {
        g.timerRemaining = Math.max(0, g.timerRemaining - dt);
        const pct = g.timerRemaining / ANSWER_TIME;
        setTimerPct(pct);
        if (g.timerRemaining <= 0) {
          g.inputQueue = 'wrong';
        }
      }

      // Pull any queued input from the React overlay. Resume world IMMEDIATELY
      // (no delay pause) — the jump fires later, at the precise moment the
      // obstacle crosses the jump-trigger line, so the arc actually clears
      // the obstacle visually.
      if (g.inputQueue && g.awaiting) {
        const kind = g.inputQueue;
        g.inputQueue = null;
        const obs = (g.awaiting as { obstacle: Obstacle }).obstacle;
        g.awaiting = false;
        obs.answered = kind;
        g.lastResult = kind;
        g.flashUntil = performance.now() + RESULT_FLASH_MS;
        setFlash(kind);
        if (kind === 'correct') {
          // Score now; jump is scheduled below against obstacle position.
          g.score += 30;
          g.correct += 1;
        } else {
          sfxRef.current.play('hit');
        }
        // Unpause immediately so obstacle starts moving; hide overlay.
        g.paused = false;
        setQuestion(null);
        if (g.alive) setPhase('playing');
        window.setTimeout(() => setFlash(null), RESULT_FLASH_MS);
      }

      if (g.paused) return;

      // Ramp speed gently over time (capped).
      g.speed = Math.min(MAX_SPEED, g.speed + SPEED_RAMP_PER_SEC * dt);

      // Advance world scroll + distance
      g.scrollX += g.speed * dt;
      g.distance += g.speed * dt * 0.04;
      g.score += dt * 6;

      // Player physics
      g.playerVy += GRAVITY * dt;
      g.playerY += g.playerVy * dt;
      const groundTop = GROUND_Y - PLAYER_R;
      if (g.playerY >= groundTop) {
        g.playerY = groundTop;
        g.playerVy = 0;
        g.grounded = true;
      } else {
        g.grounded = false;
      }

      // Run animation timer
      if (g.grounded) g.runT += dt * (g.speed / 240) * 12;

      // Blink
      g.blinkT -= dt;
      if (g.blinkT <= 0) {
        g.blink = 1;
        g.blinkT = 2 + Math.random() * 3;
      } else if (g.blink > 0) {
        g.blink = Math.max(0, g.blink - dt * 8);
      }

      // Spawn cadence
      g.spawnTimer -= dt;
      if (g.spawnTimer <= 0) {
        g.spawnTimer = SPAWN_INTERVAL;
        spawnObstacle();
      }

      // Move obstacles; handle trigger + auto-jump + clear
      for (const obs of g.obstacles) {
        obs.x -= g.speed * dt;
        // Trigger pergunta when obstacle crosses trigger line
        if (!obs.triggered && obs.answered === 'pending' && obs.x <= PLAYER_X + TRIGGER_OFFSET) {
          obs.triggered = true;
          g.paused = true;
          g.awaiting = { obstacle: obs };
          g.timerRemaining = ANSWER_TIME;
          setTimerPct(1);
          setQuestion(obs.question);
          setPhase('question');
          sfxRef.current.play('whoosh');
          break;
        }
        // Auto-jump: fires only when an ALREADY-ANSWERED-CORRECTLY obstacle
        // reaches the jump line, so the arc peak lands exactly over it.
        if (
          !obs.jumpFired &&
          obs.answered === 'correct' &&
          obs.x <= PLAYER_X + JUMP_TRIGGER_OFFSET
        ) {
          obs.jumpFired = true;
          g.playerVy = JUMP_V;
          g.grounded = false;
          sfxRef.current.play('jump');
          sfxRef.current.play('xp-gain');
          // Sparkles on takeoff
          for (let i = 0; i < 6; i++) {
            // (we push onto a simple array read in render)
          }
        }
        // Clear
        if (!obs.cleared && obs.x + obs.w < PLAYER_X - 40) {
          obs.cleared = true;
          if (obs.answered === 'correct' || obs.answered === 'wrong') {
            g.total += 1;
          }
        }
      }
      g.obstacles = g.obstacles.filter((o) => o.x > -80);

      // Collision — simple AABB of player box vs obstacle rect
      for (const obs of g.obstacles) {
        if (obs.answered === 'correct') continue;
        if (obs.cleared) continue;
        const pLeft = PLAYER_X - PLAYER_R + 4;
        const pRight = PLAYER_X + PLAYER_R - 4;
        const pTop = g.playerY - PLAYER_R + 4;
        const pBot = g.playerY + PLAYER_R - 4;
        const oLeft = obs.x;
        const oRight = obs.x + obs.w;
        const oTop = obs.y;
        const oBot = obs.y + obs.h;
        if (pRight > oLeft && pLeft < oRight && pBot > oTop && pTop < oBot) {
          onCrash(obs);
          break;
        }
      }

      // Publish HUD (throttled via rounded ints)
      setHud((prev) => {
        const score = Math.floor(g.score);
        const distance = Math.floor(g.distance);
        if (
          prev.score === score &&
          prev.distance === distance &&
          prev.hearts === g.hearts &&
          prev.correct === g.correct &&
          prev.total === g.total
        ) {
          return prev;
        }
        return { score, distance, hearts: g.hearts, correct: g.correct, total: g.total };
      });
    }

    function spawnObstacle() {
      const q = nextQuestion();
      const difficulty = q.difficulty ?? 1;
      const h = 44 + difficulty * 14;
      const w = 46;
      g.obstacles.push({
        x: WORLD_W + 20,
        y: GROUND_Y - h,
        w,
        h,
        question: q,
        answered: 'pending',
        triggered: false,
        jumpFired: false,
        cleared: false,
      });
    }

    function onCrash(obs: Obstacle) {
      sfxRef.current.play('hit');
      sfxRef.current.play('heart-loss');
      g.hearts -= 1;
      obs.cleared = true;
      // knock back: small hop + remove obstacle so we don't chain-hit
      g.playerVy = -380;
      g.obstacles = g.obstacles.filter((o) => o !== obs);
      if (g.hearts <= 0) {
        g.alive = false;
        const finalScore = Math.floor(g.score);
        const result = commitScore(HIGH_SCORE_ID, finalScore);
        setHighScore(result.best);
        setNewRecord(result.isNew);
        setPhase('over');
      }
    }

    function render() {
      const ctx = canvas!.getContext('2d');
      if (!ctx) return;
      // Reset the DPR transform that sizeCanvas set, then re-apply to make
      // render re-entrant safe.
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Background + ground
      drawBackground(ctx, WORLD_W, WORLD_H, GROUND_Y, g.scrollX, g.scrollX);
      drawGround(ctx, WORLD_W, GROUND_Y, GROUND_H, g.scrollX);

      // Obstacles
      for (const obs of g.obstacles) {
        ctx.fillStyle = 'rgb(238, 85, 85)';
        roundRect(ctx, obs.x, obs.y, obs.w, obs.h, 6);
        ctx.fill();
        ctx.fillStyle = 'rgba(120, 20, 20, 0.55)';
        roundRect(ctx, obs.x, obs.y + obs.h - 10, obs.w, 10, 6);
        ctx.fill();
        // Difficulty pips
        const difficulty = obs.question.difficulty ?? 1;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
        for (let i = 0; i < difficulty; i++) {
          ctx.beginPath();
          ctx.arc(obs.x + obs.w / 2, obs.y + 12 + i * 10, 3, 0, Math.PI * 2);
          ctx.fill();
        }
        // Outline
        ctx.strokeStyle = 'rgb(180, 40, 40)';
        ctx.lineWidth = 2;
        roundRect(ctx, obs.x + 1, obs.y + 1, obs.w - 2, obs.h - 2, 6);
        ctx.stroke();
      }

      // Player
      const runAngle = Math.sin(g.runT) * 0.06;
      const squash = g.grounded ? Math.max(0, Math.sin(g.runT * 2)) * 0.08 : 0;
      drawSlingu(ctx, PLAYER_X, g.playerY, {
        radius: PLAYER_R,
        angle: g.grounded ? runAngle : Math.max(-0.25, Math.min(0.35, g.playerVy / 1600)),
        squash,
        blink: g.blink,
        wingPhase: g.runT * 0.8,
        wingOpen: g.grounded ? 0.5 : 0.9,
        mouth: g.grounded ? 'smile' : 'open',
        groundY: GROUND_Y,
      });
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      window.removeEventListener('resize', resize);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startedRef.current = false;
    };
  }, [running, nextQuestion]);

  useEffect(() => {
    if (phase !== 'over') return;
    sfxRef.current.play('game-over');
    const xp = Math.floor(hud.score / 3);
    const t = window.setTimeout(() => {
      onExitRef.current({
        score: hud.score,
        distance: hud.distance,
        xp,
        correctCount: hud.correct,
        total: hud.total,
      });
    }, 2200);
    return () => clearTimeout(t);
  }, [phase, hud]);

  const answer = useCallback(
    (choiceIdx: number) => {
      if (!question) return;
      const correct = choiceIdx === question.answerIndex;
      gameRef.current.inputQueue = correct ? 'correct' : 'wrong';
    },
    [question],
  );

  return (
    <div className="sl-quizrun">
      <div className="sl-quizrun__hud">
        <button
          type="button"
          className="sl-quizrun__close"
          onClick={() =>
            onExitRef.current({
              score: hud.score,
              distance: hud.distance,
              xp: Math.floor(hud.score / 3),
              correctCount: hud.correct,
              total: hud.total,
            })
          }
          aria-label="Fechar"
        >
          ×
        </button>
        <div className="sl-quizrun__stats">
          <span className="sl-quizrun__hearts">
            {'♥'.repeat(hud.hearts)}
            {'·'.repeat(Math.max(0, 3 - hud.hearts))}
          </span>
          <span className="sl-quizrun__score">{hud.score} pts</span>
          <span className="sl-quizrun__distance">{hud.distance} m</span>
          <SfxToggle variant="icon" />
        </div>
      </div>

      <canvas ref={canvasRef} className="sl-quizrun__canvas" />

      {phase === 'menu' ? (
        <div className="sl-quizrun__menu">
          <h2>Quiz Run</h2>
          <p>
            Slingu corre sem parar. Quando um obstáculo chega perto o mundo{' '}
            <strong>pausa</strong> e a pergunta aparece — tempo livre pra ler.
            Acerta, ele pula e passa limpo. Erra, ele tromba. 3 colisões e acabou.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} pontos</strong>
            </p>
          ) : null}
          <button
            type="button"
            className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() => {
              sfx.play('start');
              setNewRecord(false);
              setPhase('playing');
            }}
          >
            Começar
          </button>
        </div>
      ) : null}

      {phase === 'question' && question ? (
        <div className="sl-quizrun__overlay">
          <div className="sl-quizrun__question">
            <span className="sl-quizrun__eyebrow">
              PERGUNTA · nível {question.difficulty ?? 1}/3
            </span>
            <div className="sl-quizrun__timer" aria-hidden>
              <div
                className={`sl-quizrun__timer-bar${timerPct < 0.3 ? ' sl-quizrun__timer-bar--danger' : ''}`}
                style={{ width: `${Math.round(timerPct * 100)}%` }}
              />
            </div>
            <h3>{question.prompt}</h3>
            <div className="sl-quizrun__choices">
              {question.choices.map((c, i) => (
                <button
                  key={c}
                  type="button"
                  className="sl-choice"
                  onClick={() => {
                    sfx.play('tap');
                    answer(i);
                  }}
                >
                  <span className="sl-choice__label">{c}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {flash ? (
        <div className={`sl-quizrun__flash sl-quizrun__flash--${flash}`}>
          {flash === 'correct' ? 'PULOU!' : 'TROMBOU!'}
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-quizrun__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : 'Fim da corrida'}</h2>
          <p>
            <strong>{hud.score}</strong> pontos · <strong>{hud.distance}</strong> metros
            <br />
            Acertos: {hud.correct}/{hud.total}
          </p>
          <p className="sl-quizrun__xp">+{Math.floor(hud.score / 3)} XP</p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} pontos</strong>
            {newRecord ? ' ✨' : null}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
